import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "react-js-pagination";
import {
  getActivityLogsApi,
  getActivityStatsApi,
  getActivityAdminListApi,
  clearOldLogsApi,
} from "../../services/activityLog";
import { toastMessage } from "../../utils/toastMessage";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import NotFound from "../../components/not-found/NotFound";
import common from "../../routes/common";
import { user as userRoutes, consultant as consultantRoutes } from "../../routes/route";
dayjs.extend(relativeTime);

const { NOT_FOUND } = common;

const ACTION_COLOR = {
  VIEW: "#888", CREATE: "#16a34a", UPDATE: "#d97706", DELETE: "#dc2626",
  LOGIN: "#7c3aed", LOGOUT: "#888", EXPORT: "#0891b2", DOWNLOAD: "#0284c7",
  UPLOAD: "#0d9488", SEARCH: "#475569", APPROVE: "#16a34a", REJECT: "#dc2626",
  BLOCK: "#dc2626", UNBLOCK: "#16a34a", OTHER: "#888",
  OTP_SEND: "#7c2d12", OTP_VERIFY: "#a16207",
};
const ALL_ACTIONS = Object.keys(ACTION_COLOR);

const todayStr = () => dayjs().format("YYYY-MM-DD");

// Time with seconds + AM/PM
const fmtTime  = (d) => dayjs(d).format("DD MMM YYYY, hh:mm:ss A");
const fmtShort = (d) => dayjs(d).format("hh:mm:ss A");

function formatSettlementAuditBlock(audit) {
  if (!audit || typeof audit !== "object") return "";
  const lines = [];
  lines.push(`Settlement sheet ${audit.action === "update" ? "updated" : "uploaded"}`);
  if (audit.period) lines.push(`Pay month: ${audit.period}`);
  if (audit.version) lines.push(`Sheet version: ${audit.version}`);
  if (audit.editedBy || audit.editedByEmail) {
    lines.push(`Edited by: ${audit.editedBy || "—"}${audit.editedByEmail ? ` (${audit.editedByEmail})` : ""}`);
  }
  lines.push(`Rows: ${audit.totalProcessed ?? 0} | created: ${audit.createdCount ?? 0} | updated: ${audit.updatedCount ?? 0}`);
  lines.push(`Paid: ₹${Number(audit.totalPaid || 0).toFixed(2)} | remaining add-on: ₹${Number(audit.totalPending || 0).toFixed(2)} (${audit.pendingCount ?? 0} consultants)`);
  if (Array.isArray(audit.rowChanges) && audit.rowChanges.length) {
    lines.push("Changed consultants:");
    audit.rowChanges.slice(0, 12).forEach((row) => {
      const name = row.name || row.userId || "—";
      const beforePaid = Number(row.before?.paidAmount || 0).toFixed(2);
      const afterPaid = Number(row.after?.paidAmount || 0).toFixed(2);
      const beforePending = Number(row.before?.pendingBalance || 0).toFixed(2);
      const afterPending = Number(row.after?.pendingBalance || 0).toFixed(2);
      lines.push(`  • ${name}: paid ₹${beforePaid} → ₹${afterPaid}; remaining ₹${beforePending} → ₹${afterPending}`);
    });
  }
  lines.push(
    `Server response: ${audit.responseSuccess ? "success" : "failed"}${
      audit.apiMessage ? ` — ${audit.apiMessage}` : ""
    }`
  );
  return lines.join("\n");
}

/** Rich block for consultant payout payment-modal audit logs */
function formatPaymentTrackingAuditBlock(audit) {
  if (!audit || typeof audit !== "object") return "";
  const fmtD = (iso) => {
    if (!iso) return "—";
    const d = dayjs(iso);
    return d.isValid() ? d.format("DD MMM YYYY") : String(iso);
  };
  const lines = [];
  if (audit.consultantName || audit.consultantId) {
    lines.push(`Consultant: ${audit.consultantName || "—"} (ID: ${audit.consultantId || "—"})`);
  }
  if (audit.month) lines.push(`Settlement month: ${audit.month}`);
  if (audit.previous && typeof audit.previous === "object") {
    lines.push(
      `Before — date: ${fmtD(audit.previous.paymentDate)}; status: ${audit.previous.paymentStatus ?? "—"}; due: ${
        audit.previous.dueAmount != null && audit.previous.dueAmount !== "" ? audit.previous.dueAmount : "—"
      }; reason: ${audit.previous.dueOrHoldReason || "—"}`
    );
  }
  if (audit.applied && typeof audit.applied === "object") {
    lines.push(
      `After  — date: ${fmtD(audit.applied.paymentDate)}; status: ${audit.applied.paymentStatus ?? "—"}; due: ${
        audit.applied.dueAmount != null && audit.applied.dueAmount !== "" ? audit.applied.dueAmount : "—"
      }; reason: ${audit.applied.dueOrHoldReason || "—"}`
    );
  }
  if (Array.isArray(audit.fieldChanges) && audit.fieldChanges.length > 0) {
    lines.push("Field changes:");
    audit.fieldChanges.forEach((ch) => {
      lines.push(`  • ${ch.field}: ${JSON.stringify(ch.oldValue)} → ${JSON.stringify(ch.newValue)}`);
    });
  }
  lines.push(
    `Server response: ${audit.responseSuccess ? "success" : "failed"}${
      audit.apiMessage ? ` — ${audit.apiMessage}` : ""
    }${audit.apiCode != null ? ` (code ${audit.apiCode})` : ""}`
  );
  return lines.join("\n");
}

/** Exact old/new values for consultant profile edits (also works for old logs). */
function getEffectiveConsultantChanges(audit) {
  const normalize = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (Array.isArray(value)) return value.map(normalize);
    if (typeof value === "object") return Object.keys(value).sort().reduce((result, key) => {
      result[key] = normalize(value[key]);
      return result;
    }, {});
    if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) return Number(value);
    return value;
  };
  return (Array.isArray(audit?.fieldChanges) ? audit.fieldChanges : []).filter(change => {
    let oldValue = change.oldValue;
    let newValue = change.newValue;
    // Compatibility with older logs, where price was incorrectly compared as
    // the complete object against the submitted default-price value.
    if (change.field === "price" && oldValue && typeof oldValue === "object" && typeof newValue !== "object") {
      oldValue = oldValue.default;
    }
    if (change.field === "score" && (newValue === null || newValue === "")) {
      newValue = 0;
    }
    return JSON.stringify(normalize(oldValue)) !== JSON.stringify(normalize(newValue));
  });
}

function formatConsultantEditAuditBlock(audit) {
  if (!audit || typeof audit !== "object") return "";
  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (Array.isArray(value)) return value.length ? value.map(formatValue).join(", ") : "[]";
    if (typeof value === "object") {
      try { return JSON.stringify(value); } catch { return String(value); }
    }
    return String(value);
  };
  const changes = getEffectiveConsultantChanges(audit);
  if (changes.length === 0) return "No field-level change detected.";
  return changes
    .map(change => `${change.field || "Unknown field"}: ${formatValue(change.oldValue)} → ${formatValue(change.newValue)}`)
    .join("\n");
}

function consultantEditDescription(log) {
  const description = log.description || "Consultant profile edited";
  // Detailed changes are rendered in the Response column. Remove the old,
  // request-derived key list because it may contain unchanged form fields.
  return description.split(/;\s*changed(?:\s*[—:]|\s)/i)[0].replace(/[.;\s]+$/, "");
}

function formatRequestAuditBlock(audit) {
  if (!audit || typeof audit !== "object") return "";
  const lines = [];
  if (audit.consultantName || audit.consultantId) {
    lines.push(`Consultant: ${audit.consultantName || "—"} (ID: ${audit.consultantId || "—"})`);
  }
  if (audit.requestType) lines.push(`Request type: ${audit.requestType}`);
  if (audit.decision) lines.push(`Action: ${audit.decision}`);
  const changes = Array.isArray(audit.fieldChanges) ? audit.fieldChanges : [];
  changes.forEach(change => {
    const oldValue = change.oldValue === null || change.oldValue === undefined || change.oldValue === "" ? "—" : String(change.oldValue);
    const newValue = change.newValue === null || change.newValue === undefined || change.newValue === "" ? "—" : String(change.newValue);
    lines.push(`${change.field}: ${oldValue} → ${newValue}`);
  });
  if (changes.length === 0) lines.push("No data field changed.");
  return lines.join("\n");
}

function formatRefundAuditBlock(audit) {
  if (!audit || typeof audit !== "object") return "";
  return [
    `Waitlist: ${audit.waitlistId || "—"}`,
    `User refund: ${audit.userRefundAmount ?? 0}`,
    `Consultant deduction: ${audit.consultantDeductionAmount ?? 0}`,
    audit.userMessage ? `User message: ${audit.userMessage}` : "",
    audit.consultantMessage ? `Consultant message: ${audit.consultantMessage}` : "",
  ].filter(Boolean).join("\n");
}

function formatExportAuditBlock(audit) {
  if (!audit || typeof audit !== "object") return "";
  const filters = Object.entries(audit.filters || {}).filter(([, value]) => value !== "" && value != null);
  return [
    audit.report ? `Report: ${audit.report}` : "",
    ...filters.map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : String(value)}`),
  ].filter(Boolean).join("\n") || "Exported without filters.";
}

function formatSettingsAuditBlock(audit) {
  if (!audit || typeof audit !== "object") return "";
  const changes = Array.isArray(audit.fieldChanges) ? audit.fieldChanges : [];
  return changes.length
    ? changes.map(change => `${change.field}: ${JSON.stringify(change.oldValue) ?? "—"} → ${JSON.stringify(change.newValue) ?? "—"}`).join("\n")
    : "No setting value changed.";
}

function formatActionAuditBlock(audit) {
  if (!audit || typeof audit !== "object") return "";
  const formatValue = value => {
    if (value === null || value === undefined || value === "") return "—";
    const text = typeof value === "object" ? JSON.stringify(value) : String(value);
    return text.length > 300 ? `${text.slice(0, 297)}...` : text;
  };
  const lines = [];
  const values = Object.entries(audit.values || {});
  if (values.length) {
    lines.push("Action values:");
    values.forEach(([field, value]) => lines.push(`${field}: ${formatValue(value)}`));
  }
  (audit.uploadedFiles || []).forEach(file => {
    lines.push(`Uploaded ${file.field}: ${file.fileName || "file"}${file.size != null ? ` (${file.size} bytes)` : ""}`);
  });
  if (!lines.length && audit.responseMessage) lines.push(audit.responseMessage);
  return lines.join("\n") || "—";
}

// ─── Confirm modal ────────────────────────────────────────────────────────────
const Modal = ({ onClose, children }) => (
  <div
    onClick={e => e.target === e.currentTarget && onClose()}
    style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.35)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 }}
  >
    <div style={{ background:"#fff", borderRadius:8, padding:"22px 24px", width:380, position:"relative", boxShadow:"0 6px 24px rgba(0,0,0,0.13)" }}>
      <button onClick={onClose} style={{ position:"absolute", top:12, right:14, background:"none", border:"none", fontSize:18, cursor:"pointer", color:"#aaa" }}>✕</button>
      {children}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
//  LEVEL 2 — Detail view for one admin
// ─────────────────────────────────────────────────────────────────────────────
const AdminDetail = ({ admin, dateRange, onBack }) => {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [pagination, setPag]    = useState({ page:1, limit:10, total:0, totalPages:1 });
  const moduleSet               = useRef(new Set());
  const [filters, setFilters]   = useState({
    action: "", module: "", status: "",
    startDate: dateRange.start || todayStr(),
    endDate:   dateRange.end   || todayStr(),
    search: "",
  });
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const fetchLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getActivityLogsApi({
        page, limit: pagination.limit,
        adminId: admin._id,
        ...filters,
      });
      if (res?.data?.success) {
        res.data.result.logs.forEach(l => moduleSet.current.add(l.module));
        setLogs(res.data.result.logs);
        setPag(p => ({ ...p, ...res.data.result.pagination }));
      }
    } catch { toastMessage("Failed to load logs", "error"); }
    finally { setLoading(false); }
  }, [filters, pagination.limit, admin._id]);

  useEffect(() => { fetchLogs(1); }, [filters]);

  const applyQuick = (preset) => {
    const today = todayStr();
    if (preset === "today") setFilters(f => ({ ...f, startDate: today, endDate: today }));
    if (preset === "week")  setFilters(f => ({ ...f, startDate: dayjs().subtract(6,"day").format("YYYY-MM-DD"), endDate: today }));
    if (preset === "month") setFilters(f => ({ ...f, startDate: dayjs().subtract(29,"day").format("YYYY-MM-DD"), endDate: today }));
  };

  const resetFilters = () => setFilters({
    action:"", module:"", status:"",
    startDate: dateRange.start || todayStr(),
    endDate: dateRange.end || todayStr(),
    search:"",
  });

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap" }}>
          <button
            onClick={onBack}
            style={{ background:"none", border:"1px solid #ddd", borderRadius:4, padding:"5px 12px", fontSize:13, cursor:"pointer", color:"#555", display:"flex", alignItems:"center", gap:5 }}
          >
            ← Back
          </button>
          <div>
            <h4 className="Title" style={{ margin:0 }}>Activity Detail</h4>
            <div style={{ fontSize:12, color:"#888", marginTop:2 }}>
              <b style={{ color:"#FF8989" }}>{admin.adminName || admin.adminEmail}</b>
              {admin.adminName && <span style={{ marginLeft:6 }}>{admin.adminEmail}</span>}
            </div>
          </div>
        </div>

        {/* Filters — Row 1: search + dropdowns + dates */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:10, alignItems:"center", marginBottom:8 }}>
          <input
            className="form-control"
            placeholder="Search module, description…"
            value={filters.search}
            onChange={e => setF("search", e.target.value)}
            style={{ width:210 }}
          />
          <select className="form-control" value={filters.action} onChange={e => setF("action", e.target.value)} style={{ width:140 }}>
            <option value="">All Actions</option>
            {ALL_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="form-control" value={filters.module} onChange={e => setF("module", e.target.value)} style={{ width:190 }}>
            <option value="">All Modules</option>
            {[...moduleSet.current].sort().map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="form-control" value={filters.status} onChange={e => setF("status", e.target.value)} style={{ width:130 }}>
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
          <input type="date" className="form-control" value={filters.startDate}
            onChange={e => setF("startDate", e.target.value)} style={{ width:148 }} />
          <input type="date" className="form-control" value={filters.endDate}
            onChange={e => setF("endDate", e.target.value)} style={{ width:148 }} />
        </div>

        {/* Filters — Row 2: quick date buttons */}
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12 }}>
          {["today","week","month"].map(p => (
            <button
              key={p}
              onClick={() => applyQuick(p)}
              style={{ padding:"6px 16px", fontSize:13, cursor:"pointer", border:"1px solid #cbcaca", borderRadius:5, background:"#fff", color:"#444", whiteSpace:"nowrap" }}
            >
              {p === "today" ? "Today" : p === "week" ? "This Week" : "This Month"}
            </button>
          ))}
          <button
            onClick={resetFilters}
            style={{ padding:"6px 16px", fontSize:13, cursor:"pointer", border:"1px solid #cbcaca", borderRadius:5, background:"#fff", color:"#888", whiteSpace:"nowrap" }}
          >
            Reset
          </button>
        </div>

        <div style={{ marginBottom:8 }}>
          <span style={{ fontSize:13, color:"#666" }}>
            {loading ? "Loading…" : <><b>{pagination.total.toLocaleString()}</b> records found</>}
          </span>
        </div>

        {/* Table */}
        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Date &amp; Time</th>
                <th>Description</th>
                <th>Response</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign:"center", padding:30, color:"#999" }}>Loading…</td></tr>
              ) : logs.length === 0 ? (
                <NotFound cols={6} msg="No activity found!" icon={NOT_FOUND} />
              ) : (
                logs.map((log, i) => {
                  const uid   = log.metadata?.targetUserId   || (log.resourceType === "User"       ? log.resourceId : "");
                  const uname = log.metadata?.targetUserName || (log.resourceType === "User"       ? log.resourceName : "");
                  const cid   = log.metadata?.targetConsultantId   || (log.resourceType === "Consultant" ? log.resourceId : "");
                  const cname = log.metadata?.targetConsultantName || (log.resourceType === "Consultant" ? log.resourceName : "");
                  const sno   = (pagination.page - 1) * pagination.limit + i + 1;

                  return (
                    <tr key={log._id || i}>
                      <td >{sno}</td>

                      <td style={{ whiteSpace:"nowrap" }}>
                        <div>{dayjs(log.createdAt).format("DD MMM YYYY")} {fmtShort(log.createdAt)}</div>
                      </td>

                      <td>
                        {(() => {
                          // Build list of {name, url} tokens to render as links
                          const links = [];
                          if (uid   && uname) links.push({ name: uname, href: `${userRoutes.USER_DETAIL}/${uid}` });
                          if (cid   && cname) links.push({ name: cname, href: `${consultantRoutes.CONSULTANT_DETAIL}/${cid}` });

                          const visibleDescription = log.metadata?.consultantEditAudit
                            ? consultantEditDescription(log)
                            : (log.description || "—");
                          if (links.length === 0) return visibleDescription;

                          // Split description by known names and interleave <Link> elements
                          let parts = [visibleDescription];
                          links.forEach(({ name, href }) => {
                            parts = parts.flatMap(part => {
                              if (typeof part !== "string" || !part.includes(name)) return [part];
                              const idx = part.indexOf(name);
                              return [
                                part.substring(0, idx),
                                <Link key={href} to={href} style={{ color:"#FF8989" }}>{name}</Link>,
                                part.substring(idx + name.length),
                              ].filter(p => p !== "");
                            });
                          });
                          return <>{parts}</>;
                        })()}
                      </td>

                      <td style={{ verticalAlign: "top", maxWidth: 400 }}>
                        {log.metadata?.paymentTrackingAudit || log.metadata?.consultantEditAudit ||
                         log.metadata?.requestAudit || log.metadata?.refundAudit || log.metadata?.exportAudit ||
                         log.metadata?.settingsAudit || log.metadata?.actionAudit ? (
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontFamily: "system-ui, sans-serif",
                              fontSize: 12,
                              color: "#475569",
                              lineHeight: 1.45,
                            }}
                          >
                            {log.metadata?.consultantEditAudit
                              ? formatConsultantEditAuditBlock(log.metadata.consultantEditAudit)
                              : log.metadata?.settlementAudit
                                ? formatSettlementAuditBlock(log.metadata.settlementAudit)
                              : log.metadata?.paymentTrackingAudit
                                ? formatPaymentTrackingAuditBlock(log.metadata.paymentTrackingAudit)
                                : log.metadata?.requestAudit
                                  ? formatRequestAuditBlock(log.metadata.requestAudit)
                                  : log.metadata?.refundAudit
                                    ? formatRefundAuditBlock(log.metadata.refundAudit)
                                    : log.metadata?.exportAudit
                                      ? formatExportAuditBlock(log.metadata.exportAudit)
                                      : log.metadata?.settingsAudit
                                        ? formatSettingsAuditBlock(log.metadata.settingsAudit)
                                        : formatActionAuditBlock(log.metadata.actionAudit)}
                          </pre>
                        ) : (
                          log.metadata?.responseMessage || "—"
                        )}
                      </td>

                      <td>
                        <span style={{ color: log.status === "success" ? "#16a34a" : "#dc2626" }}>
                          {log.status === "success" ? "Success" : "Failed"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination.total > 10 && (
          <div className="pagination-wrapper">
            <div className="Pagination">
              <Pagination
                activePage={pagination.page}
                itemsCountPerPage={pagination.limit}
                totalItemsCount={pagination.total}
                pageRangeDisplayed={5}
                onChange={(page) => fetchLogs(page)}
                hideDisabled={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  LEVEL 1 — Admin summary list
// ─────────────────────────────────────────────────────────────────────────────
const ActivityLog = () => {
  const [selectedAdmin, setSelectedAdmin]   = useState(null);
  const [detailDateRange, setDetailDateRange] = useState({ start: todayStr(), end: todayStr() });

  const [adminBreakdown, setAdminBreakdown] = useState([]);
  const [statsLoading, setStatsLoading]     = useState(false);
  const [showClear, setShowClear]           = useState(false);
  const [clearDays, setClearDays]           = useState(90);
  const [clearing, setClearing]             = useState(false);
  const [dateRange, setDateRange]           = useState({ start: todayStr(), end: todayStr() });

  const fetchAdmins = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await getActivityStatsApi({
        startDate: dateRange.start,
        endDate:   dateRange.end,
      });
      if (res?.data?.success) {
        setAdminBreakdown(res.data.result.adminBreakdown || []);
      }
    } catch { toastMessage("Failed to load data", "error"); }
    finally { setStatsLoading(false); }
  }, [dateRange]);

  useEffect(() => { fetchAdmins(); }, [dateRange]);

  const applyQuick = (preset) => {
    const today = todayStr();
    if (preset === "today") setDateRange({ start: today, end: today });
    if (preset === "week")  setDateRange({ start: dayjs().subtract(6,"day").format("YYYY-MM-DD"), end: today });
    if (preset === "month") setDateRange({ start: dayjs().subtract(29,"day").format("YYYY-MM-DD"), end: today });
  };

  const openDetail = (admin) => {
    setDetailDateRange({ start: dateRange.start, end: dateRange.end });
    setSelectedAdmin(admin);
  };

  const doClear = async () => {
    setClearing(true);
    try {
      const res = await clearOldLogsApi(clearDays);
      if (res?.data?.success) {
        toastMessage(res.data.message, "success");
        setShowClear(false);
        fetchAdmins();
      }
    } catch { toastMessage("Failed to clear logs", "error"); }
    finally { setClearing(false); }
  };

  // ── If an admin is selected, show their detail view ──────────────────────
  if (selectedAdmin) {
    return (
      <AdminDetail
        admin={selectedAdmin}
        dateRange={detailDateRange}
        onBack={() => setSelectedAdmin(null)}
      />
    );
  }

  // ── Level 1: Admin summary ────────────────────────────────────────────────
  return (
    <div className="WrapperArea">
      <div className="WrapperBox">

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:8 }}>
          <h4 className="Title" style={{ margin:0,marginBottom:20 }}>Activity Management Logs</h4>
          {/* <button onClick={() => setShowClear(true)} style={{ background:"#fff", color:"#FF8989", border:"1px solid #FF8989", padding:"6px 14px", borderRadius:4, fontSize:13, cursor:"pointer" }}>
            Clear Old Logs
          </button> */}
        </div>

        {/* Date filters */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, flexWrap:"wrap" }}>
          <span style={{ fontSize:13, color:"#555", whiteSpace:"nowrap" }}>From</span>
          <input type="date" className="form-control" value={dateRange.start}
            onChange={e => setDateRange(d => ({ ...d, start: e.target.value }))}
            style={{ width:145 }} />

          <span style={{ fontSize:13, color:"#555", whiteSpace:"nowrap" }}>To</span>
          <input type="date" className="form-control" value={dateRange.end}
            onChange={e => setDateRange(d => ({ ...d, end: e.target.value }))}
            style={{ width:145 }} />

          <div style={{ display:"flex", gap:6, marginLeft:6 }}>
            {["today","week","month"].map(p => (
              <button
                key={p}
                onClick={() => applyQuick(p)}
                style={{
                  padding:"6px 14px", fontSize:13, cursor:"pointer",
                  border:"1px solid #cbcaca", borderRadius:5,
                  background:"#fff", color:"#444",
                  whiteSpace:"nowrap",
                }}
              >
                {p === "today" ? "Today" : p === "week" ? "This Week" : "This Month"}
              </button>
            ))}
          </div>
        </div>

        {/* Subtitle */}
        <div style={{ marginBottom:10 }}>
          <span style={{ fontSize:13, color:"#666" }}>
            {statsLoading ? "Loading…" : (
              <>
                <b>{adminBreakdown.length}</b> admin{adminBreakdown.length !== 1 ? "s" : ""} active
                {" "}from <b>{dayjs(dateRange.start).format("DD MMM")}</b>
                {dateRange.start !== dateRange.end && <> to <b>{dayjs(dateRange.end).format("DD MMM YYYY")}</b></>}
                {dateRange.start === dateRange.end && <b> {dayjs(dateRange.start).format("YYYY")}</b>}
              </>
            )}
          </span>
        </div>

        {/* Admin table */}
        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Admin</th>
                <th>Role</th>
                <th>Total Actions</th>
                <th>First Active</th>
                <th>Last Active</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {statsLoading ? (
                <tr><td colSpan={7} style={{ textAlign:"center", padding:30, color:"#999" }}>Loading…</td></tr>
              ) : adminBreakdown.length === 0 ? (
                <NotFound cols={7} msg="No admin activity found for this date range!" icon={NOT_FOUND} />
              ) : (
                adminBreakdown.map((a, i) => (
                  <tr key={a._id}>
                    <td style={{ color:"#FF8989" }}>{i + 1}</td>

                    <td>
                      <div>{a.adminName || "—"}</div>
                      <div style={{ fontSize:11, color:"#888" }}>{a.adminEmail}</div>
                    </td>

                    <td>
                      {a.adminRole === "admin" ? "Super Admin" : "Sub Admin"}
                    </td>

                    <td style={{ color:"#FF8989" }}>
                      {(a.count || 0).toLocaleString()}
                    </td>

                    <td style={{ whiteSpace:"nowrap" }}>
                      {a.firstActivity ? (
                        <>
                          <div>{dayjs(a.firstActivity).format("DD MMM YYYY")} {fmtShort(a.firstActivity)}</div>
                         
                        </>
                      ) : "—"}
                    </td>

                    <td style={{ whiteSpace:"nowrap" }}>
                      {a.lastActivity ? (
                        <>
                          <div>{dayjs(a.lastActivity).format("DD MMM YYYY")} {fmtShort(a.lastActivity)}</div>
                         
                          {/* <div style={{ fontSize:11, color:"#aaa" }}>{dayjs(a.lastActivity).fromNow()}</div> */}
                        </>
                      ) : "—"}
                    </td>

                    <td>
                      <button
                        onClick={() => openDetail(a)}
                        style={{ background:"#FF8989", color:"#fff", border:"none", padding:"5px 14px", borderRadius:4, fontSize:12, cursor:"pointer" }}
                      >
                        View Detail
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Clear modal */}
      {showClear && (
        <Modal onClose={() => setShowClear(false)}>
          <h5 style={{ margin:"0 0 10px", color:"#222" }}>Clear Old Logs</h5>
          <p style={{ fontSize:13, color:"#666", margin:"0 0 14px", lineHeight:1.6 }}>
            Permanently delete logs older than the specified days. <b>This cannot be undone.</b>
          </p>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
            <input type="number" min={1} max={365} value={clearDays} onChange={e => setClearDays(e.target.value)}
              className="form-control" style={{ width:90, fontSize:14 }} />
            <span style={{ color:"#666", fontSize:13 }}>days old</span>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => setShowClear(false)} style={{ flex:1, padding:"7px", border:"1px solid #ddd", background:"#fff", color:"#666", borderRadius:4, cursor:"pointer", fontSize:13 }}>Cancel</button>
            <button onClick={doClear} disabled={clearing} style={{ flex:1, padding:"7px", background:"#FF8989", color:"#fff", border:"none", borderRadius:4, cursor:"pointer", fontSize:13, opacity: clearing ? 0.7 : 1 }}>
              {clearing ? "Deleting…" : "Delete Logs"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ActivityLog;
